import { fetchEmailCampaignCustomersThunk } from '@spike/email-marketing-action';
import { EmailCampaignCustomer, EmailCampaignPopulation } from '@spike/email-marketing-model';
import ChooseModalFooter from 'components/UI/ChooseModal/Footer';
import ChooseModalHeader from 'components/UI/ChooseModal/Header';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import clsx from 'clsx';
import { useApiClientWrapper } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import { throttle } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import styles from './EditClientsDrawer.module.scss';

const PAGE_SIZE = 30;
// Scroll position (as a fraction of the scrollable height) at which we
// auto-fetch the next page. 0.9 matches the SMS picker behaviour so the
// load feels seamless without firing when the user is still browsing.
const AUTO_LOAD_SCROLL_THRESHOLD = 0.9;
// Throttle the scroll handler so we don't fire a fetch for every pixel — same
// 150ms cadence as the SMS picker's `onListScroll`.
const SCROLL_THROTTLE_MS = 150;

type FilterTab = 'all' | 'selected' | 'unselected';

interface EditClientsDrawerProps {
    show: boolean;
    onClose: () => void;
    /**
     * Emits the selection from the drawer when the user clicks Save.
     *   - `selected === 'all'`   → exclude mode. `excluded` is the list of
     *     ids to drop from the resolved pool (`exclude_ids` on the BE).
     *   - `selected === number[]` → include mode. The wizard sends those ids
     *     as `customer_ids` directly; `excluded` is dropped because the BE
     *     materialises the positive list as-is.
     */
    onSave: (selected: 'all' | number[], excluded: number[]) => void;
    /**
     * Seeds the drawer's selection mode each time it opens. `'all'` puts the
     * Select All toggle on (exclude mode); a number[] puts it off and pre-checks
     * exactly those ids (include mode). Mirror of `values.selectedClientIds`.
     */
    initialSelectedIds: 'all' | number[];
    /**
     * Mirror of the form's `excludedClientIds` — used to seed the local
     * excluded set each time the drawer opens in exclude mode. Ignored when
     * `initialSelectedIds` is an array (include mode).
     */
    initialExcludedIds: number[];
    /**
     * Campaign criteria for the customer fetch. `undefined` means "all
     * email-marketable customers" — we intentionally omit the param so the
     * BE falls back to its `with_email` default. Passing `criteria=all`
     * explicitly hits a controller-side dispatch bug (`.send('all', nil)`
     * collides with ActiveRecord's `.all`); SMS sidesteps the same trap by
     * also omitting the param when the user picks All Clients.
     *
     * `'by_tag'` is the only other supported value reaching this drawer —
     * the parent gates the Edit button so maqueta criteria never get here.
     */
    criteria?: 'by_tag';
    /** Comma-separated tag ids when `criteria === 'by_tag'`. */
    args?: string;
    /**
     * Scopes the picker to the same recipient population the wizard is on
     * (clients / leads / both). Forwarded as a query param on every
     * customers fetch so the drawer never surfaces recipients outside the
     * selected pool. Defaults to `"both"` when omitted.
     */
    population?: EmailCampaignPopulation;
}

/**
 * Drawer for adjusting the recipient list of a campaign. Brings the picker
 * to feature parity with the SMS campaigns drawer (`ChooseModalClients` +
 * `ChooseModalClientResults`):
 *
 *   - **Two modes** controlled by the Select All toggle in the table head:
 *       Exclude mode (toggle ON, default)  — every client receives the
 *       campaign by default; unchecking a row drops them into `excludedSet`.
 *       Include mode (toggle OFF)          — nobody receives by default;
 *       checking a row adds them to `includedSet`. Save emits the positive
 *       list and the wizard sends it as `customer_ids` (no `criteria`), so
 *       the BE materialises EXACTLY those clients.
 *
 *   - **Tab filters** with live counters (All / Selected / Unselected) so the
 *     user can audit what they've picked before saving. The Selected counter
 *     is the single most important signal — it shows how many clients will
 *     actually receive the campaign.
 *
 *   - **Auto-load on scroll** at ~90% of the visible list height (All tab
 *     only — Selected / Unselected are filters over already-loaded rows, so
 *     additional pages don't add visible items there). Replaces the old
 *     manual "Load more" button.
 *
 * Pagination accumulates clients across pages (deduped by id) so the user
 * can re-check selections from earlier pages without scrolling them off
 * screen. Search hits the campaign-aware `/email_campaigns/customers?search=`
 * endpoint and respects the active `criteria` / `args`.
 */
const EditClientsDrawer: React.FC<EditClientsDrawerProps> = ({
    show,
    onClose,
    onSave,
    initialSelectedIds,
    initialExcludedIds,
    criteria,
    args,
    population
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const customers = useSelector((state: RootState) => state.emailCampaigns.customers);

    // Selection mode is encoded by the SHAPE of `selectedIds`:
    //   - `'all'`     → exclude mode (Select All ON). Row toggles fill
    //                   `excludedIds`; checked rows are NOT in that set.
    //   - `Set<number>` → include mode (Select All OFF). Row toggles fill
    //                     `selectedIds` directly; `excludedIds` is unused.
    const [selectedIds, setSelectedIds] = React.useState<'all' | Set<number>>('all');
    const [excludedIds, setExcludedIds] = React.useState<Set<number>>(new Set());

    const [accumulatedClients, setAccumulatedClients] = React.useState<EmailCampaignCustomer[]>([]);
    const [page, setPage] = React.useState<number>(1);
    const [activeTab, setActiveTab] = React.useState<FilterTab>('all');

    // Search query — deliberately NOT reset on `show` so the user's typed
    // query persists across an accidental close+reopen. Resetting would
    // surprise them mid-task; the parent (RecipientsStep) doesn't gate on
    // search state so persistence is harmless.
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // ------------------------------------------------------------------
    // Reset + fetch on open. We intentionally only react to `show` going
    // true — internal updates to the initial-* / criteria / args props while
    // the drawer is open would surprise the user mid-edit. Those values are
    // captured at the moment of opening.
    //
    // The current `debouncedSearchQuery` is passed through so reopening
    // after a previous session resumes with the same filter applied.
    // ------------------------------------------------------------------
    React.useEffect(() => {
        if (!show) return;
        setSelectedIds(initialSelectedIds === 'all' ? 'all' : new Set(initialSelectedIds));
        setExcludedIds(new Set(initialExcludedIds));
        setActiveTab('all');
        setAccumulatedClients([]);
        setPage(1);
        dispatch(
            fetchEmailCampaignCustomersThunk(apiClientWrapper, {
                criteria,
                args,
                page: 1,
                size: PAGE_SIZE,
                search: debouncedSearchQuery || undefined,
                population
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // ------------------------------------------------------------------
    // Re-fetch on debounced search change while drawer is open. Resets
    // accumulator + page so the new query starts from scratch instead of
    // appending to the previous result set. The selection state is NOT
    // touched — the user's in-progress toggles persist across searches.
    // ------------------------------------------------------------------
    React.useEffect(() => {
        if (!show) return;
        setAccumulatedClients([]);
        setPage(1);
        dispatch(
            fetchEmailCampaignCustomersThunk(apiClientWrapper, {
                criteria,
                args,
                page: 1,
                size: PAGE_SIZE,
                search: debouncedSearchQuery || undefined,
                population
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery]);

    // ------------------------------------------------------------------
    // Merge slice updates into the local accumulator. Dedupe by id to
    // tolerate (a) the multi-tag size-1 counter fetch overwriting the slice
    // while we're open, and (b) accidental double-fires of the load-more
    // path.
    // ------------------------------------------------------------------
    React.useEffect(() => {
        if (!show) return;
        if (customers.list.length === 0) return;

        setAccumulatedClients(prev => {
            const seen = new Set(prev.map(c => c.id));
            const additions = customers.list.filter(c => !seen.has(c.id));
            if (additions.length === 0) return prev;
            return [...prev, ...additions];
        });
    }, [customers.list, show]);

    // ------------------------------------------------------------------
    // Derived flags
    // ------------------------------------------------------------------
    const isAllSelected = selectedIds === 'all';
    const totalRecords = customers.totalRecords ?? 0;
    const hasMore = customers.totalPages != null && page < customers.totalPages;
    const isInitialLoading = customers.loading && accumulatedClients.length === 0;
    const isEmpty = !customers.loading && accumulatedClients.length === 0 && totalRecords === 0;

    // ------------------------------------------------------------------
    // Live counters. In exclude mode "Selected" = total − excluded; in
    // include mode "Selected" = size of the positive list. Counters reflect
    // the GLOBAL state, not what's on screen — the user can have made
    // selections on previously loaded pages and still see the right number.
    // ------------------------------------------------------------------
    const selectedCount = isAllSelected ? Math.max(0, totalRecords - excludedIds.size) : selectedIds.size;
    const unselectedCount = isAllSelected ? excludedIds.size : Math.max(0, totalRecords - selectedIds.size);

    // ------------------------------------------------------------------
    // Per-row "is this client currently in the will-receive bucket?"
    // ------------------------------------------------------------------
    const isClientChecked = React.useCallback(
        (id: number): boolean => {
            if (isAllSelected) {
                return !excludedIds.has(id);
            }
            return (selectedIds as Set<number>).has(id);
        },
        [isAllSelected, excludedIds, selectedIds]
    );

    // ------------------------------------------------------------------
    // Visible list — filtered by the active tab. We always filter over the
    // accumulated set so toggles made on already-loaded earlier pages
    // remain authoritative for the count.
    // ------------------------------------------------------------------
    const visibleClients = React.useMemo<EmailCampaignCustomer[]>(() => {
        if (activeTab === 'all') return accumulatedClients;
        if (activeTab === 'selected') {
            return accumulatedClients.filter(c => isClientChecked(c.id));
        }
        // unselected
        return accumulatedClients.filter(c => !isClientChecked(c.id));
    }, [accumulatedClients, activeTab, isClientChecked]);

    // ------------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------------

    // Select All header toggle. Flipping it WIPES the opposite mode's set
    // because mixing them would lie about which clients actually receive.
    const toggleSelectAll = () => {
        if (isAllSelected) {
            // From exclude mode → include mode. Start with nothing selected
            // and an empty exclude set.
            setSelectedIds(new Set());
            setExcludedIds(new Set());
        } else {
            // From include mode → exclude mode. Reset to "everyone receives,
            // nobody excluded".
            setSelectedIds('all');
            setExcludedIds(new Set());
        }
    };

    const toggleClient = (id: number) => {
        if (isAllSelected) {
            // Exclude mode — toggling drops from / adds to the excluded set.
            setExcludedIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        } else {
            // Include mode — toggling adds to / removes from the positive list.
            setSelectedIds(prev => {
                const current = prev as Set<number>;
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        }
    };

    const handleLoadMore = React.useCallback(() => {
        if (customers.loading) return;
        if (!hasMore) return;
        const nextPage = page + 1;
        setPage(nextPage);
        dispatch(
            fetchEmailCampaignCustomersThunk(apiClientWrapper, {
                criteria,
                args,
                page: nextPage,
                size: PAGE_SIZE,
                search: debouncedSearchQuery || undefined,
                population
            })
        );
    }, [
        customers.loading,
        hasMore,
        page,
        criteria,
        args,
        debouncedSearchQuery,
        population,
        dispatch,
        apiClientWrapper
    ]);

    // Auto-load when the user scrolls near the bottom. Throttled so we don't
    // fire on every pixel; only active on the "All" tab because Selected /
    // Unselected are pure filters and loading additional pages doesn't add
    // visible rows to those views.
    const handleBodyScroll = React.useMemo(
        () =>
            throttle((event: React.UIEvent<HTMLDivElement>) => {
                if (activeTab !== 'all') return;
                const el = event.currentTarget;
                if (!el) return;
                const reached = el.scrollTop + el.clientHeight >= el.scrollHeight * AUTO_LOAD_SCROLL_THRESHOLD;
                if (reached) handleLoadMore();
            }, SCROLL_THROTTLE_MS),
        [activeTab, handleLoadMore]
    );

    // Cancel any pending throttled invocations on unmount so a late fire
    // doesn't try to dispatch against a stale instance.
    React.useEffect(() => {
        return () => {
            handleBodyScroll.cancel();
        };
    }, [handleBodyScroll]);

    const handleSave = () => {
        const selectedOut: 'all' | number[] = isAllSelected ? 'all' : Array.from(selectedIds);
        const excludedOut: number[] = Array.from(excludedIds);
        onSave(selectedOut, excludedOut);
    };

    return (
        <div className={clsx(styles.wrapper, { [styles.hidden]: !show })}>
            <div
                className={styles.backdrop}
                onClick={onClose}
            />

            <div
                className={styles.drawer}
                role="dialog"
                aria-modal="true"
                aria-label="Edit clients"
            >
                <ChooseModalHeader
                    title="Edit clients"
                    subtitle="Uncheck clients you want to exclude, or toggle off Select All and pick the ones you want to include."
                    variant="clients"
                    onClose={onClose}
                    onSearch={setSearchQuery}
                />

                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'all' })}
                        onClick={() => setActiveTab('all')}
                    >
                        All ({totalRecords})
                    </button>
                    <button
                        type="button"
                        className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'selected' })}
                        onClick={() => setActiveTab('selected')}
                    >
                        Selected ({selectedCount})
                    </button>
                    <button
                        type="button"
                        className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'unselected' })}
                        onClick={() => setActiveTab('unselected')}
                    >
                        Unselected ({unselectedCount})
                    </button>
                </div>

                <div
                    className={styles.body}
                    onScroll={handleBodyScroll}
                >
                    {isInitialLoading && (
                        <div className={styles.centeredFill}>
                            <Spinner />
                        </div>
                    )}

                    {isEmpty && (
                        <div className={styles.centeredFill}>
                            <p className={styles.emptyText}>No clients match these criteria.</p>
                        </div>
                    )}

                    {!isEmpty && !isInitialLoading && (
                        <>
                            <div className={styles.tableHead}>
                                <label className={styles.tableHeadLabel}>
                                    <input
                                        type="checkbox"
                                        className={styles.checkbox}
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                    />
                                    <span className={styles.tableHeadText}>Select all</span>
                                </label>
                            </div>

                            <ul className={styles.list}>
                                {visibleClients.map(client => {
                                    const isChecked = isClientChecked(client.id);
                                    return (
                                        <li
                                            key={client.id}
                                            className={styles.row}
                                        >
                                            <label className={styles.rowLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={styles.checkbox}
                                                    checked={isChecked}
                                                    onChange={() => toggleClient(client.id)}
                                                />
                                                <span className={styles.rowText}>
                                                    <span className={styles.name}>
                                                        {client.firstName} {client.lastName}
                                                    </span>
                                                    <span className={styles.email}>{client.email}</span>
                                                </span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>

                            {customers.loading && (
                                <div className={styles.loadingRow}>
                                    <Spinner />
                                </div>
                            )}
                        </>
                    )}
                </div>

                <ChooseModalFooter
                    onCancel={onClose}
                    onSave={handleSave}
                />
            </div>
        </div>
    );
};

export default EditClientsDrawer;
